// Provider deep-link safety (music playlist / "Open in provider").
//
// Requirement: validate EVERY provider link before window.open — HTTPS only,
// allowlisted provider domains, recognized resource patterns, and no arbitrary
// redirect URLs. Anything else is rejected (never opened). Mirrors the meeting-
// provider detection convention (calendarApi.detectMeetingProvider).
export type MusicProvider = 'apple_music' | 'spotify' | 'youtube_music';

interface Rule {
  provider: MusicProvider;
  host: RegExp;     // exact/allowlisted host
  path?: RegExp;    // recognized resource path
  requiresList?: boolean; // YouTube Music playlists carry ?list=
}

// Allowlist — first match wins. Only these hosts + resource shapes are openable.
const RULES: Rule[] = [
  { provider: 'apple_music', host: /^music\.apple\.com$/i, path: /\/(playlist|album)\//i },
  { provider: 'spotify', host: /^(open\.)?spotify\.com$/i, path: /\/(playlist|album|track)\//i },
  { provider: 'youtube_music', host: /^music\.youtube\.com$/i, path: /^\/(playlist|watch)$/i, requiresList: true },
];

export interface ClassifiedLink {
  provider: MusicProvider;
  canonicalUrl: string; // origin + safe path (+ ?list for YT) — query stripped otherwise
}

// classifyProviderUrl returns a safe, normalized link or null. Rejects non-HTTPS,
// non-allowlisted hosts, unrecognized paths, and strips arbitrary query params so a
// tracking/redirect payload can never ride along.
export function classifyProviderUrl(raw: string): ClassifiedLink | null {
  let u: URL;
  try { u = new URL(raw); } catch { return null; }
  if (u.protocol !== 'https:') return null;          // HTTPS only
  if (u.username || u.password) return null;          // no embedded credentials
  for (const r of RULES) {
    if (!r.host.test(u.hostname)) continue;
    if (r.path && !r.path.test(u.pathname)) return null;   // recognized resource pattern
    let canonical = `https://${u.hostname}${u.pathname}`;
    if (r.requiresList) {
      const list = u.searchParams.get('list');
      if (!list || !/^[A-Za-z0-9_-]{5,60}$/.test(list)) return null;
      canonical += `?list=${encodeURIComponent(list)}`;   // keep ONLY the list id
    }
    return { provider: r.provider, canonicalUrl: canonical };
  }
  return null; // not an allowlisted provider link
}

export function isSafeProviderUrl(raw: string): boolean {
  return classifyProviderUrl(raw) !== null;
}

// openProviderUrl validates then opens in a new tab (noopener). Returns false when
// the link is unsafe (caller shows a message); never opens an unvalidated URL.
export function openProviderUrl(raw: string): boolean {
  const c = classifyProviderUrl(raw);
  if (!c) return false;
  window.open(c.canonicalUrl, '_blank', 'noopener,noreferrer');
  return true;
}
